import { faGlobe, faMoon, faSun, faTimes } from "@fortawesome/free-solid-svg-icons";
import React, { ReactElement, useContext, useEffect } from "react";
import { ProgressTracker as BaseProgressTracker, Button, Link } from "../common/components";
import { corners, gap, marginBottom, media, paddingX } from "../common/styles";
import { emitEvent, useViewportSize } from "../utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";
import { ActionType } from "../AppDataProvider/actions";
import { AppContext } from "../AppDataProvider/AppDataProvider";
import { ThemeToggleContext } from "../ThemeToggleContext";
import i18n from "../i18n";
export function Header(): ReactElement {
  const viewport = useViewportSize();
  const progressInHeader = viewport.width >= media.thresholds.progressInHeader && viewport.width < media.thresholds.desktop;
  const {
    t
  } = useTranslation();
  const {
    appDataDispatch,
    appData
  } = useContext(AppContext);
  const {
    activeThemeName,
    allowVisitorThemeOverride,
    setVisitorOverride
  } = useContext(ThemeToggleContext);
  const theme = useTheme();
  const {
    logoUrl
  } = theme.brand;
  const urlParams = new URLSearchParams(window.location.search);
  const iFrameId = urlParams.get("iFrameId") ?? undefined;
  const inPopoverMode = !!urlParams.get("inPopoverMode");
  useEffect(() => {
    if (iFrameId) {
      emitEvent({
        data: {
          iFrameId: iFrameId
        },
        name: "READY"
      });
    }
  }, [iFrameId]);
  const closePopover = (iFrameId: string) => {
    emitEvent({
      data: {
        iFrameId: iFrameId
      },
      name: "CLOSE_APPOINTEDD_POPOVER"
    });
  };

  // ESC Key handling
  useEffect(() => {
    const keydownHandler = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (appData.nonModalDialog) {
          return appDataDispatch({
            type: ActionType.HIDE_NON_MODAL_DIALOG
          });
        }
        if (inPopoverMode && iFrameId) {
          closePopover(iFrameId);
        }
      }
    };
    document.addEventListener("keydown", keydownHandler);
    return () => {
      document.removeEventListener("keydown", keydownHandler);
    };
  }, [appData.nonModalDialog, appDataDispatch, iFrameId, inPopoverMode]);
  const showLanguageSelectorButton = !!urlParams.get("enableLanguageSelector");
  const showCloseButton = inPopoverMode;
  const showThemeToggleButton = allowVisitorThemeOverride;
  const showHeader = progressInHeader || showLanguageSelectorButton || !!(showCloseButton && iFrameId) || showThemeToggleButton || !!logoUrl;
  const toggleTheme = () => {
    setVisitorOverride(activeThemeName === "dark" ? "light" : "dark");
  };
  return <HeaderContainer {...{
    showHeader
  }} data-sentry-element="HeaderContainer" data-sentry-component="Header" data-sentry-source-file="Header.tsx">
      {progressInHeader && <ProgressTracker />}
      {logoUrl && <BrandLogo src={logoUrl} alt={t("brandLogoAltText", "Company logo")} />}
      <HeaderControls data-sentry-element="HeaderControls" data-sentry-source-file="Header.tsx">
        {showLanguageSelectorButton && <LanguageSelector>
            <FontAwesomeIcon icon={faGlobe} />
            <LanguageLink aria-label={t("languageSelectorAriaLabel")} onClick={() => {
          appDataDispatch({
            payload: {
              variant: "languageSelector"
            },
            type: ActionType.SHOW_NON_MODAL_DIALOG
          });
        }}>
              {i18n.language}
            </LanguageLink>
          </LanguageSelector>}
        {showThemeToggleButton && <ThemeToggleButton type="button" aria-label={activeThemeName === "dark" ? t("switchToLightThemeButtonLabel") : t("switchToDarkThemeButtonLabel")} onClick={toggleTheme}>
            <FontAwesomeIcon icon={activeThemeName === "dark" ? faSun : faMoon} />
          </ThemeToggleButton>}
        {showCloseButton && iFrameId && <CloseButton className="popoverCloseButton" aria-label={t("closePopoverButtonLabel")} onClick={() => closePopover(iFrameId)}>
            <FontAwesomeIcon icon={faTimes} />
          </CloseButton>}
      </HeaderControls>
    </HeaderContainer>;
}
const HeaderContainer = styled.div<{
  showHeader: boolean;
}>`
  display: ${({
  showHeader
}) => showHeader ? "grid" : "none"};
  grid-template-columns: auto 1fr auto;
  align-items: center;
  ${marginBottom.s}
  @media ${media.desktop} {
    ${marginBottom.m}
  }
`;
const ProgressTracker = styled(BaseProgressTracker)`
  grid-column: 2;
  justify-self: center;
`;
const BrandLogo = styled.img`
  grid-column: 1;
  justify-self: start;
  align-self: center;
  max-height: 2.25rem;
  max-width: 8rem;
  object-fit: contain;
`;

/**
 * Right-hand cluster of header controls (language selector, theme toggle,
 * close button), grouped into a single grid column so each control's
 * visibility (and the resulting layout) doesn't depend on manually
 * recomputing which grid-column index every OTHER conditionally-rendered
 * control should occupy.
 */
const HeaderControls = styled.div`
  grid-column: 3;
  justify-self: right;
  display: flex;
  flex-direction: row;
  align-items: center;
  ${gap.s}
  padding: 0.25rem;
`;
const LanguageSelector = styled.div`
  height: 2.25rem;

  border: 2px solid ${({
  theme
}) => theme.brand.cssVars["--d-border"]};
  ${corners.m}
  ${paddingX.s}

  text-transform: uppercase;
  color: ${({
  theme
}) => theme.brand.cssVars["--d-text"]};

  display: flex;
  flex-direction: row;
  align-items: center;

  ${gap.xs}

  @media ${media.hoverable} {
    &:hover {
      filter: brightness(0.96);
    }
  }
`;
const LanguageLink = styled(Link)`
  color: inherit;
  border-bottom: none;
  background: none;

  &:focus {
    outline: none;
    border-bottom: none;
    background: none;
  }

  @media ${media.hoverable} {
    &:hover {
      background: none;
      border-bottom: none;
    }
  }
`;
const ThemeToggleButton = styled(Button).attrs({
  variant: "tertiary"
})`
  width: 2.25rem;
  height: 2.25rem;
`;
const CloseButton = styled(Button).attrs({
  variant: "tertiary"
})`
  width: 2.25rem;
  height: 2.25rem;
`;
export default Header;